/// <amd-module name="Test/TestPlansHubRefresh/TestCaseLinkedItems/Component/TestCaseLinkedBugsConnectedComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import * as Constants from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Constants";
import * as Resources from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Resources";
import { ILinkedItemsAwareState, ILinkedWorkItem } from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Redux/TestCaseLinkedItemsContracts";
import { ITfsPageService } from "Tfs/Platform/Page";
import { CustomCard } from "VSSUI/Card";
import { IVssPageContext } from "VSS/Platform/Context";
import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { IListSelection, ListSelection, ISimpleListCell, renderListCell } from "VSSUI/List";
import { routeUrl } from "VSS/Platform/Location";
import { Components, IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ITableColumn, Table, renderSimpleCell, SimpleTableCell } from "VSSUI/Table";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface ITestCaseLinkedBugsProps extends IVssComponentProperties {
    linkedBugs: ILinkedWorkItem[];
}

/**
 * TestCaseLinkedBugsConnectedComponent: Renders the bugs tab in linked items panel
 */
export class TestCaseLinkedBugsConnectedComponent extends VssComponent<ITestCaseLinkedBugsProps, IVssComponentContext> {
    private _tableColumns: ITableColumn<any>[];
    private _pageContext: IVssPageContext;
    private _selection: IListSelection;
    private _items: ObservableArray<ILinkedWorkItem>;

    constructor(props: ITestCaseLinkedBugsProps, context?: IVssComponentContext) {
        super(props, context);
        this._tableColumns = this._getColumns();
        this._pageContext = context!.pageContext;
        this._selection = new ListSelection(true);
        this._items = new ObservableArray<ILinkedWorkItem>();
    }

    public shouldComponentUpdate(nextProps: ITestCaseLinkedBugsProps) {
        if (nextProps.linkedBugs) {
            this._items.removeAll();
            for (let i = 0; i < nextProps.linkedBugs.length; i++) {
                this._items.push(nextProps.linkedBugs[i]);
            }
        }

        return true;
    }

    public render(): JSX.Element {
        let card = <Spinner className="spinner-card flex-grow" size={SpinnerSize.medium} />;
        if (this.props.linkedBugs) {
            card = (
                <CustomCard className="testplans-hub-linked-panel-card">
                    <Table
                        className="testplans-hub-linked-panel-table"
                        containerClassName="custom-scrollbar"
                        columns={this._tableColumns}
                        itemProvider={this._items}
                        showLines={true}
                        scrollable={true}
                        singleClickActivation={false}
                        onActivate={this._openWorkItem}
                        selection={this._selection}
                    />
                </CustomCard>
            );
        }
        
        return <div className="flex-grow">{card}</div>;
    }

    private _getColumns(): ITableColumn<any>[] {
        const tableColumns: ITableColumn<any>[] = [];
        tableColumns.push(
            {
                id: Constants.WorkItemIdColumnId,
                name: Resources.WorkItemIdColumnName,
                width: new ObservableValue(100),
                onSize: this._onColumnSize,
                renderCell: this._renderIdColumn
            },
            {
                id: Constants.TitleColumnId,
                name: Resources.TitleColumnName,
                width: new ObservableValue(170),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.AssignedToColumnId,
                name: Resources.AssignedToColumnName,
                width: new ObservableValue(100),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.StateColumnId,
                name: Resources.StateColumnName,
                width: new ObservableValue(50),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.LatestUpdateColumnId,
                name: Resources.LatestUpdateColumnName,
                width: new ObservableValue(150),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            }
        );
        return tableColumns;
    }

    private _renderIdColumn(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<any>, tableItem: ILinkedWorkItem): JSX.Element {
        const iconClassName: string = "bowtie-icon flex-noshrink " + Constants.WorkItemIconClassNameMap[tableItem.workItemType];
        let simpleListCell: ISimpleListCell = {
            iconProps: {
                iconName: "icon_insect",
                className: iconClassName
            },
            text: String(tableItem.id)
        };
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                className="bolt-table-cell-content flex-row flex-center linked-items-table-cell"
            >
                {renderListCell(simpleListCell)}
            </SimpleTableCell>
        );
    }

    private _onColumnSize = (event: MouseEvent, columnIndex: number, width: number) => {
        const originalColumnWidth = this._tableColumns[columnIndex].width;
        (originalColumnWidth as ObservableValue<number>).value = width;
    };

    private _openWorkItem = (): void => {
        const index = this._selection.value[0].beginIndex;
        const openWIRouteId = "ms.vss-work-web.work-items-form-route-with-id";
        const tfsPageService = this._pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectName: string = "";

        if (tfsPageData && tfsPageData.project) {
            projectName = tfsPageData.project.name;
        }

        const workItemUrl: string =
            projectName && projectName != ""
                ? routeUrl(this._pageContext, openWIRouteId, {
                      project: projectName,
                      id: `${this.props.linkedBugs[index].id}`
                  })
                : "";
        const openedWindow = window.open(workItemUrl, "_blank");
        if (openedWindow) {
            openedWindow.opener = null;
        }
    };
}

function mapStateToProps(state: ILinkedItemsAwareState): Partial<ITestCaseLinkedBugsProps> {
    let linkedWorkItems;
    if (state && state.linkedItemsState) {
        linkedWorkItems = state.linkedItemsState.linkedBugsList;
    }
    return {
        linkedBugs: linkedWorkItems
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestCaseLinkedBugsProps> {
    return {
        dispatch
    };
}

export const TestCaseLinkedBugsComponent = connect(mapStateToProps, mapDispatchToProps)(TestCaseLinkedBugsConnectedComponent);

Components.add("testcase-linked-items-bugs-panel", TestCaseLinkedBugsComponent);
